import React, { useEffect, useState } from 'react';
import {
  Card, Typography, Button, Empty, Spin, Alert, Row, Col,
  Popconfirm, Input, message, Modal, Tag,
} from 'antd';
import {
  MailOutlined, DeleteOutlined, SearchOutlined,
  PlusOutlined, FileTextOutlined, EyeOutlined,
  StarOutlined, FolderOpenOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Text } = Typography;

const Templates = () => {
  const [templates,    setTemplates]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [search,       setSearch]       = useState('');
  const [previewTpl,   setPreviewTpl]   = useState(null); // template being previewed

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const navigate = useNavigate();

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/templates', { headers });
      setTemplates(res.data);
    } catch {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/templates/${id}`, { headers });
      setTemplates(prev => prev.filter(t => t.id !== id));
      message.success('Template deleted');
    } catch {
      message.error('Failed to delete template');
    }
  };

  const filtered = templates.filter(t =>
    `${t.name} ${t.category || ''}`.toLowerCase().includes(search.toLowerCase())
  );
  const isSystem = t => t.is_system || t.org_id == null;
  const starters = filtered.filter(isSystem);
  const own      = filtered.filter(t => !isSystem(t));

  const useTemplate = tpl => navigate('/campaigns/create', { state: { templateDesign: tpl.design_json } });

  const renderCard = (tpl) => {
    const system = isSystem(tpl);
    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={tpl.id}>
        <Card
          style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', height: '100%' }}
          styles={{ body: { padding: 0 } }}
        >
          {/* Thumbnail preview area — click to open full preview */}
          <div
            onClick={() => setPreviewTpl(tpl)}
            style={{ height: 180, borderRadius: 'var(--r-lg) var(--r-lg) 0 0', overflow: 'hidden', cursor: 'pointer', background: 'var(--surface-2)', position: 'relative', border: '0' }}
          >
            {tpl.html ? (
              <iframe
                srcDoc={tpl.html}
                title={tpl.name}
                sandbox="allow-same-origin"
                scrolling="no"
                style={{ width: '600px', height: '900px', border: 'none', pointerEvents: 'none', transformOrigin: 'top left', transform: 'scale(0.38)' }}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <MailOutlined style={{ fontSize: 40, color: 'var(--brand-primary)', opacity: 0.4 }} />
              </div>
            )}
            {system && tpl.category && (
              <Tag color="purple" style={{ position: 'absolute', top: 10, left: 10, borderRadius: 6, margin: 0, fontSize: 11 }}>{tpl.category}</Tag>
            )}
          </div>

          {/* Card body */}
          <div style={{ padding: '14px 16px' }}>
            <Text strong style={{ fontSize: 13, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 2, color: 'var(--text)' }}>
              {tpl.name}
            </Text>
            <Text style={{ fontSize: 12, color: 'var(--text-3)', display: 'block', marginBottom: 14, minHeight: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: system ? 'inherit' : 'var(--font-mono)' }}>
              {system
                ? (tpl.description || 'Starter template')
                : new Date(tpl.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </Text>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="small" icon={<EyeOutlined />} onClick={() => setPreviewTpl(tpl)} style={{ borderRadius: 'var(--r-md)', fontSize: 12, flex: 1 }}>
                Preview
              </Button>
              <Button size="small" icon={<PlusOutlined />} onClick={() => useTemplate(tpl)} style={{ borderRadius: 'var(--r-md)', fontSize: 12, flex: 1, borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)' }}>
                Use
              </Button>
              {!system && (
                <Popconfirm title="Delete this template?" onConfirm={() => handleDelete(tpl.id)} okText="Delete" okButtonProps={{ danger: true }} cancelText="Cancel">
                  <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 'var(--r-md)', flexShrink: 0 }} />
                </Popconfirm>
              )}
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  const SectionHeading = ({ icon, title, count }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 2px 14px' }}>
      {icon}
      <Text strong style={{ fontSize: 15, color: 'var(--text)' }}>{title}</Text>
      <Text style={{ color: 'var(--text-3)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>{count}</Text>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <Text style={{ color: 'var(--text-2)' }}>Saved designs you can reuse across campaigns</Text>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/campaigns/create')}
          style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}
        >
          New Campaign
        </Button>
      </div>

      {error && (
        <Alert message={error} type="error" showIcon closable
          onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }} />
      )}

      {/* Search bar */}
      <Card style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', marginBottom: 20, boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Input
            prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
            placeholder="Search templates…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ borderRadius: 'var(--r-md)', maxWidth: 360 }}
          />
          <Text style={{ color: 'var(--text-3)', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
            {filtered.length} template{filtered.length !== 1 ? 's' : ''}
          </Text>
        </div>
      </Card>

      {loading ? (
        <div style={{ textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>
      ) : filtered.length === 0 ? (
        <Card style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', textAlign: 'center', padding: '60px 0' }}>
          <Empty
            image={<FileTextOutlined style={{ fontSize: 56, color: 'var(--brand-primary)', opacity: 0.3 }} />}
            imageStyle={{ height: 60 }}
            description={
              <Text style={{ fontSize: 15, color: 'var(--text)' }}>
                {search ? 'No templates match your search' : 'No templates yet'}
              </Text>
            }
          />
        </Card>
      ) : (
        <>
          {/* Starter templates — system, read-only */}
          {starters.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <SectionHeading icon={<StarOutlined style={{ color: 'var(--brand-primary)' }} />} title="Starter templates" count={`${starters.length}`} />
              <Row gutter={[16, 16]}>{starters.map(renderCard)}</Row>
            </div>
          )}

          {/* Your templates — saved by the org */}
          <SectionHeading icon={<FolderOpenOutlined style={{ color: 'var(--text-2)' }} />} title="Your templates" count={`${own.length}`} />
          {own.length > 0 ? (
            <Row gutter={[16, 16]}>{own.map(renderCard)}</Row>
          ) : (
            <Card style={{ borderRadius: 'var(--r-lg)', border: '1px dashed var(--border)', textAlign: 'center', padding: '32px 0' }}>
              <Text style={{ color: 'var(--text-3)', fontSize: 13 }}>
                {search
                  ? 'None of your saved templates match your search'
                  : 'You haven’t saved any templates yet. Use a starter above, or open the email builder and click “Save as Template”.'}
              </Text>
            </Card>
          )}
        </>
      )}

      {/* ── Full preview modal ── */}
      <Modal
        open={!!previewTpl}
        onCancel={() => setPreviewTpl(null)}
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
            <span>{previewTpl?.name}</span>
            <Button
              type="primary" size="small" icon={<PlusOutlined />}
              onClick={() => {
                navigate('/campaigns/create', { state: { templateDesign: previewTpl.design_json } });
                setPreviewTpl(null);
              }}
              style={{ borderRadius: 8, fontSize: 12 }}
            >
              Use Template
            </Button>
          </div>
        }
        footer={null}
        width="80vw"
        style={{ top: 24 }}
        styles={{ body: { padding: 0, height: '78vh', overflow: 'hidden' } }}
        destroyOnHidden
      >
        {previewTpl?.html ? (
          <iframe
            srcDoc={previewTpl.html}
            title={previewTpl.name}
            sandbox="allow-same-origin"
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Empty description="No preview available for this template" />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Templates;
